import React, { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { PageSection, Product, SiteTheme } from '../../../types';
import { db } from '../../../services/db';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ProductCard } from '../../store/ProductCard';
import { pickShowcaseProducts, showcaseLimit } from '../../../services/showcase';
import { pinGroupProducts } from '../../../services/shop';

interface ProductShowcaseProps {
    section: PageSection;
    theme?: SiteTheme;
    /** Butikens produkter, om de redan är hämtade. */
    products?: Product[];
    /** Klick på ett kort. I säljarens butik öppnar det produkten; utan det går man till butiken. */
    onSelect?: (product: Product) => void;
    /** Gruppens butik: produkter som bara finns här (t.ex. Bankerydspaketet) läggs först. */
    classId?: string;
}

/**
 * Bästsäljare: produkter som admin valt, i admins ordning, eller de mest
 * sålda senaste 30 dagarna. Ett klick på kortet öppnar produkten.
 */
export const ProductShowcase: React.FC<ProductShowcaseProps> = ({ section, theme, products: given, onSelect, classId }) => {
    const navigate = useNavigate();
    const [fetched, setFetched] = useState<Product[]>([]);

    useEffect(() => {
        if (given) return;
        db.getProducts().then(data => setFetched(data.filter(p => !!p.categoryId))).catch(() => setFetched([]));
    }, [given]);

    const { data: bestselling = [] } = useSWR(
        section.productSource === 'bestselling' ? 'bestselling-products' : null,
        () => db.getBestsellingProductIds(30, 6),
        { revalidateOnFocus: false },
    );

    const all = given ?? fetched;
    const shown = useMemo(
        () => pinGroupProducts(pickShowcaseProducts(all, section, bestselling), all, classId, showcaseLimit(section)),
        [all, section, bestselling, classId]);
    const select = onSelect ?? (() => navigate('/store'));
    // Kolumnerna följer hur många som faktiskt visas, så att korten inte blir smala.
    const count = Math.min(shown.length || showcaseLimit(section), showcaseLimit(section));
    const cols = count === 4 ? 'md:grid-cols-4' : count <= 2 ? 'md:grid-cols-2 max-w-3xl mx-auto' : 'md:grid-cols-3';

    if (all.length > 0 && shown.length === 0) return null;

    return (
        <section className="py-16 md:py-24 px-4 md:px-6 bg-background" aria-labelledby={`showcase-${section.id}`}>
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-10 md:mb-14">
                    <h2 id={`showcase-${section.id}`} className="text-3xl md:text-4xl font-heading font-bold text-primary mb-4">
                        {section.title || 'Bästsäljare'}
                    </h2>
                    {section.text && (
                        <p className="text-secondary max-w-2xl mx-auto leading-relaxed">{section.text}</p>
                    )}
                </div>

                <ol className={`grid grid-cols-2 ${cols} gap-4 md:gap-8 mb-10 md:mb-12 list-none p-0`}>
                    {shown.map((product, i) => (
                        <li key={product.id} className="h-full relative">
                            <span className="absolute top-2.5 right-2.5 z-10 bg-white/95 text-ink-900 text-label font-black px-2.5 py-1 rounded-full shadow-sm pointer-events-none" aria-label={`Plats ${i + 1}`}>
                                #{i + 1}
                            </span>
                            <ProductCard
                                product={product}
                                onAddToCart={select}
                                cardStyle={theme?.productCardStyle || 'classic'}
                            />
                        </li>
                    ))}
                </ol>

                {!onSelect && (
                    <div className="text-center">
                        <button
                            onClick={() => navigate('/store')}
                            className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-secondary transition-colors shadow-lg"
                        >
                            Till Webbutiken <ArrowRight size={16} />
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};
